// src/api/secureRequests.ts

import type {
  // Week 1 - User Profile APIs
  CreateProfileRequest,
  CreateProfileResponse,
  EditProfileRequest,
  EditProfileResponse,
  ScreenNameCheckResponse,
  // Week 2 - Pattern Management APIs
  CreateTempPatternResponse,
  PatternTempResponse,
  UpdateTempPatternRequest,
  TempToDraftMigrationRequest,
  TempToDraftMigrationResponse,
  PublishPatternResponse,
  // Image Upload APIs
  TempImageUploadResponse,
  PatternImageUploadResponse,
  ReviewImageUploadResponse,
  ProfileImageUploadResponse,
  // Draft Pattern APIs
  CreateDraftPatternResponse,
  DeleteTempPatternResponse,
  DeleteUserReviewResponse,
  RepublishPatternResponse,
  PatternVersionResponse,
  MigrateTempImagesResponse,
  // Notification APIs
  UnreadNotificationCountResponse,
  UnreadNotificationsResponse,
  PaginatedNotificationsResponse,
  MarkNotificationReadResponse,
  MarkAllNotificationsReadResponse,
  // Week 3 - Review APIs
  CreateReviewRequest,
  CreateReviewResponse,
  GetUserReviewResponse,
  FlagReviewRequest,
  FlagReviewSuccessResponse,
  FlagStatusResponse,
  // Week 4 - Social APIs
  FollowArtistSuccessResponse,
  UnfollowArtistSuccessResponse,
  AddFavoriteSuccessResponse,
  RemoveFavoriteSuccessResponse,
  GetUserRelationsResponse,
  // Draft Pattern APIs
  UpdateDraftPatternResponse,
  FetchPatternDraftResponse,
  // Moderation APIs
  ModerationQueueResponse,
  ModerationSnapshotResponse,
  ResolveModerationResponse,
  AcknowledgeAutoRejectResponse,
  PatternModerationHistoryResponse,
  AutoRejectedQueueResponse,
  RejectionTemplatesResponse,
  ReviewModerationQueueResponse,
  PendingReviewFlagsResponse,
  AutoRejectedReviewsResponse,
  ResolveReviewModerationResponse,
  ResolveFlaggedReviewResponse,
  ModeratorNotesResponse,
  CreateNoteResponse,
  PatternNotesResponse,
  SnapshotNotesResponse,
  UserBehaviorOverviewResponse,
  ComprehensiveUserAnalysisResponse,
  // Craft Room APIs
  CraftRoomResponse,
  MaterialResponse,
  CraftProjectResponse,
  ProjectMaterialResponse,
  ProjectCommentResponse,
  CreateMaterialRequest,
  CreateCraftProjectRequest,
  UpdateCraftProjectRequest,
  CreateProjectMaterialRequest,
  UpdateProjectMaterialRequest
} from '@mrb91002/shared-types';

import { SECURE_API_BASE } from './config';

/**
 * Secure authenticated fetch helper for API calls requiring a Cognito token.
 * - Handles error normalization and content-type.
 * - Always sends `Authorization: Bearer ...` and credentials.
 */
async function secureFetch(url: string, idToken: string, opts: RequestInit = {}): Promise<unknown> {
  const resp = await fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  // Try parsing JSON if possible, fallback to text
  const tryParse = async () => {
    const text = await resp.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  };

  // 204 No Content — return null/empty
  if (resp.status === 204) return null;
  
  // 202 Accepted (e.g., review pending moderation) — parse response
  if (resp.status === 202) {
    return await tryParse();
  }

  if (!resp.ok) {
    const errData = await tryParse() || 'API error';
    
    console.log('[DEBUG] secureFetch error response:', {
      status: resp.status,
      statusText: resp.statusText,
      url: url,
      errData
    });
    
    // Handle token expiration
    if (resp.status === 401) {
      const errorMessage = typeof errData === 'string' ? errData : JSON.stringify(errData);
      if (errorMessage.includes('expired') || errorMessage.includes('token')) {
        // Clear any stored auth state and redirect to home
        localStorage.removeItem('tempPatternId');
        
        // Show user-friendly message and redirect
        if (typeof window !== 'undefined') {
          alert('Your session has expired. Please log in again.');
          window.location.href = '/';
        }
        
        throw new Error('Session expired - please log in again');
      }
    }
    
    // Create error object with structured data
    const error = new Error();
    if (typeof errData === 'object' && errData !== null) {
      // Structured error response
      Object.assign(error, errData);
      error.message = errData.message || errData.details || 'API error';
      error.status = resp.status;
    } else {
      // Simple string error
      error.message = typeof errData === 'string' ? errData : JSON.stringify(errData);
      error.status = resp.status;
    }
    
    throw error;
  }

  return await tryParse();
}

// ---- FAVORITE PATTERN ----

// Used in: Home page (PatternCards component)
/**
 * Favorite a pattern (for current logged-in user)
 */
export async function addFavoritePattern(patternId: string, idToken: string): Promise<AddFavoriteSuccessResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/favorite`,
    idToken,
    { method: 'POST' }
  ) as Promise<AddFavoriteSuccessResponse>;
}

// Used in: Home page (PatternCards component)
/**
 * Unfavorite a pattern (for current logged-in user)
 */
export async function removeFavoritePattern(patternId: string, idToken: string): Promise<RemoveFavoriteSuccessResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/favorite`,
    idToken,
    { method: 'DELETE' }
  ) as Promise<RemoveFavoriteSuccessResponse>;
}

// ---- TEMP PATTERN ENDPOINTS ----

// Used in: CreatePattern page
/**
 * Create a new temp pattern with TTL
 */
export async function createTempPattern(idToken: string): Promise<CreateTempPatternResponse> {
  const result = await secureFetch(`${SECURE_API_BASE}/pattern/temp`, idToken, {
    method: 'POST',
    body: JSON.stringify({}),
  }) as CreateTempPatternResponse;
  return result;
}

// Used in: CreatePattern page
/**
 * Fetch temp pattern for session recovery
 */
export async function fetchTempPattern(patternId: string, idToken: string): Promise<PatternTempResponse> {
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`, idToken, {
    method: 'GET',
  }) as Promise<PatternTempResponse>;
}

// Used in: CreatePattern page
/**
 * Update temp pattern during editing
 */
export async function updateTempPattern(patternId: string, tempData: UpdateTempPatternRequest, idToken: string): Promise<PatternTempResponse> {
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`, idToken, {
    method: 'PUT',
    body: JSON.stringify(tempData),
  }) as Promise<PatternTempResponse>;
}

// Used in: CreatePattern page
/**
 * Delete temp pattern when starting fresh
 */
export async function deleteTempPattern(patternId: string, idToken: string): Promise<DeleteTempPatternResponse> {
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`, idToken, {
    method: 'DELETE',
  });
}

// Used in: CreatePattern page
/**
 * Upload temp pattern image with TTL
 */
export async function uploadTempPatternImage(
  patternId: string,
  file: File | Blob,
  idToken: string
): Promise<TempImageUploadResponse> {
  const filename = (file instanceof File && file.name) ? file.name : 'upload.jpg';
  const url = `${SECURE_API_BASE}/pattern/temp-image-upload?patternId=${encodeURIComponent(patternId)}&filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    credentials: 'include',
    body: file,
  });
  if (!resp.ok) {
    let errMsg = 'API error';
    try { errMsg = await resp.text(); } catch { /* empty */ }
    throw new Error(errMsg);
  }
  return resp.json() as Promise<TempImageUploadResponse>;
}

// ---- DRAFT PATTERN ENDPOINTS ----

// Used in: CreatePattern page
/**
 * Create a new draft pattern (returns patternId)
 */
export async function createDraftPattern(idToken: string, data: Record<string, unknown> = {}): Promise<CreateDraftPatternResponse> {
  // POST /api/secure/pattern/draft
  const result = await secureFetch(`${SECURE_API_BASE}/pattern/draft`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return result;
}

// Used in: CreatePattern page
// Used in: PreviewDraftPattern page
/**
 * Fetch an existing draft pattern
 */
export async function fetchPatternDraft(patternId: string, idToken: string): Promise<FetchPatternDraftResponse> {
  // GET /api/secure/pattern/{patternId}/draft
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/draft`, idToken, {
    method: 'GET',
  }) as Promise<FetchPatternDraftResponse>;
}

// Used in: CreatePattern page
/**
 * Update a draft pattern (any partial fields)
 */
export async function updateDraftPattern(patternId: string, draftData: unknown, idToken: string): Promise<UpdateDraftPatternResponse> {
  // PUT /api/secure/pattern/{patternId}/draft
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/draft`, idToken, {
    method: 'PUT',
    body: JSON.stringify(draftData),
  }) as Promise<UpdateDraftPatternResponse>;
}

// Used in: CreatePattern page
/**
 * Publish a draft as a new pattern version
 */
export async function publishPattern(patternId: string, idToken: string): Promise<PublishPatternResponse> {
  // POST /api/secure/pattern/{patternId}/publish
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/publish`, idToken, {
    method: 'POST',
  }) as Promise<PublishPatternResponse>;
}



// Used in: Pattern page
/**
 * Republish pattern from draft (creates new version)
 */
export async function republishPattern(patternId: string, idToken: string): Promise<RepublishPatternResponse> {
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/republish`, idToken, {
    method: 'POST',
  }) as Promise<RepublishPatternResponse>;
}

// Used in: Pattern page
/**
 * Get specific version of a pattern
 */
export async function getPatternVersion(patternId: string, versionNumber: number, idToken: string): Promise<PatternVersionResponse> {
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/version/${versionNumber}`, idToken, {
    method: 'GET',
  }) as Promise<PatternVersionResponse>;
}

/**
 * Unpublish pattern (remove from public)
 */
export async function unpublishPattern(patternId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/unpublish`, idToken, {
    method: 'POST',
  }) as Promise<void>;
}

// ---- IMAGE UPLOADS ----

// Used in: CreatePattern page
// Used in: ImageLeftTextRightEdit component
// Used in: TextLeftImageRightEdit component
/**
 * Upload a pattern image file (multipart, not JSON)
 */
export async function uploadPatternImage(
  patternId: string,
  file: File | Blob,
  idToken: string
): Promise<PatternImageUploadResponse> {
  const filename = (file instanceof File && file.name) ? file.name : 'upload.jpg';
  const url = `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/image-upload?filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    credentials: 'include',
    body: file,
  });
  if (!resp.ok) {
    let errMsg = 'API error';
    try { errMsg = await resp.text(); } catch { /* empty */ }
    throw new Error(errMsg);
  }
  return resp.json();
}

// Used in: CreateProfile page
/**
 * Upload a profile image (multipart, not JSON)
 */
export async function uploadProfileImage(
  file: File | Blob,
  idToken: string
): Promise<ProfileImageUploadResponse> {
  const filename = (file instanceof File && file.name) ? file.name : 'profile.jpg';
  const url = `${SECURE_API_BASE}/user/profile-image-upload?filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    credentials: 'include',
    body: file,
  });
  if (!resp.ok) {
    let errMsg = 'API error';
    try { errMsg = await resp.text(); } catch { /* empty */ }
    throw new Error(errMsg);
  }
  return resp.json();
}

// ---- PROFILE MUTATIONS ----

// Used in: CreateProfile page
/**
 * Create a new user profile (first sign-in)
 */
export async function createProfile(
  profileData: CreateProfileRequest,
  idToken: string
): Promise<CreateProfileResponse> {
  return secureFetch(`${SECURE_API_BASE}/user/profile`, idToken, {
    method: 'POST',
    body: JSON.stringify(profileData),
  }) as Promise<CreateProfileResponse>;
}

// ---- PATTERN MUTATIONS (Published patterns only, NOT for drafts) ----



// ---- REVIEW MUTATIONS ----

// Use shared type for review data
type ReviewData = CreateReviewRequest;

// Use shared type for flag data
type FlagData = Omit<FlagReviewRequest, 'patternId' | 'patternVersion'>;

// Used in: Pattern page
/**
 * Upload a review image file (multipart, not JSON)
 */
export async function uploadReviewImage(
  tempId: string,
  file: File | Blob,
  idToken: string
): Promise<ReviewImageUploadResponse> {
  const filename = (file instanceof File && file.name) ? file.name : 'upload.jpg';
  const url = `${SECURE_API_BASE}/review-images/upload?tempId=${encodeURIComponent(tempId)}&filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    credentials: 'include',
    body: file,
  });
  if (!resp.ok) {
    let errMsg = 'API error';
    try { errMsg = await resp.text(); } catch { /* empty */ }
    throw new Error(errMsg);
  }
  return resp.json();
}

// Used in: Pattern page
/**
 * Create or update a review for a pattern
 */
export async function createOrUpdateReview(
  patternId: string,
  reviewData: CreateReviewRequest,
  idToken: string
): Promise<CreateReviewResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/review`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(reviewData)
    }
  ) as Promise<CreateReviewResponse>;
}

// Used in: Pattern page
/**
 * Get user's review for a pattern
 */
export async function getUserReview(
  patternId: string,
  idToken: string
): Promise<GetUserReviewResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/my-review`,
    idToken,
    { method: 'GET' }
  ) as Promise<GetUserReviewResponse>;
}

// Used in: Pattern page
/**
 * Delete user's review for a pattern
 */
export async function deleteUserReview(
  patternId: string,
  idToken: string
): Promise<DeleteUserReviewResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/my-review`,
    idToken,
    { method: 'DELETE' }
  ) as Promise<void>;
}

// Used in: Pattern page
/**
 * Flag a review for moderation
 */
export async function flagReview(
  reviewerScreenName: string,
  patternId: string,
  version: number,
  flagData: FlagData,
  idToken: string
): Promise<FlagReviewSuccessResponse> {
  const requestData: FlagReviewRequest = {
    ...flagData,
    patternId,
    patternVersion: version
  };
  return secureFetch(
    `${SECURE_API_BASE}/review/${encodeURIComponent(reviewerScreenName)}/flag`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(requestData)
    }
  ) as Promise<FlagReviewSuccessResponse>;
}

// Used in: Pattern page
/**
 * Check if user has already flagged a review
 */
export async function getFlagStatus(
  reviewId: string,
  idToken: string
): Promise<FlagStatusResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/review/${encodeURIComponent(reviewId)}/flag-status`,
    idToken,
    { method: 'GET' }
  ) as Promise<FlagStatusResponse>;
}

// ---- USER ACTIONS ----

// Used in: Profile page
/**
 * Follow an artist (by screen name, logged-in user)
 */
export async function followUser(artistId: string, idToken: string): Promise<FollowArtistSuccessResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/follow/${encodeURIComponent(artistId)}`,
    idToken,
    { method: 'POST' }
  ) as Promise<FollowArtistSuccessResponse>;
}

// Used in: Profile page
/**
 * Unfollow an artist (by screen name, logged-in user)
 */
export async function unfollowUser(artistId: string, idToken: string): Promise<UnfollowArtistSuccessResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/follow/${encodeURIComponent(artistId)}`,
    idToken,
    { method: 'DELETE' }
  ) as Promise<UnfollowArtistSuccessResponse>;
}

// ---- NOTIFICATIONS ----

/**
 * Get unread notification count for badge
 */
export async function getUnreadNotificationCount(idToken: string): Promise<UnreadNotificationCountResponse> {
  return secureFetch(`${SECURE_API_BASE}/notifications/unread-count`, idToken, { method: 'GET' });
}

/**
 * Get unread notifications only
 */
export async function getUnreadNotifications(idToken: string, lastKey?: string): Promise<{
  notifications: Array<{
    id: string;
    type: string;
    title: string;
    preview: string;
    full_message?: string;
    created_at: string;
    read: boolean;
    priority: string;
    actions?: Array<{ label: string; url: string; style: string }>;
    metadata?: Record<string, any>;
  }>;
  lastKey?: string;
}> {
  const url = lastKey 
    ? `${SECURE_API_BASE}/notifications/unread?lastKey=${encodeURIComponent(lastKey)}`
    : `${SECURE_API_BASE}/notifications/unread`;
  return secureFetch(url, idToken, { method: 'GET' });
}

/**
 * Mark a single notification as read
 */
export async function markNotificationRead(notificationId: string, sk?: string, idToken: string): Promise<MarkNotificationReadResponse> {
  return secureFetch(`${SECURE_API_BASE}/notifications/mark-read`, idToken, {
    method: 'POST',
    body: JSON.stringify({ id: notificationId, sk }),
  }) as Promise<MarkNotificationReadResponse>;
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsRead(idToken: string): Promise<MarkAllNotificationsReadResponse> {
  return secureFetch(`${SECURE_API_BASE}/notifications/mark-all-read`, idToken, {
    method: 'POST',
  }) as Promise<MarkAllNotificationsReadResponse>;
}



// Used in: EditProfile page
/**
 * Update user profile (never allows editing screen_name)
 */
export async function updateProfile(
  profileData: EditProfileRequest,
  idToken: string
): Promise<EditProfileResponse> {
  return secureFetch(`${SECURE_API_BASE}/user/profile`, idToken, {
    method: 'PUT',
    body: JSON.stringify(profileData),
  }) as Promise<EditProfileResponse>;
}

// ---- USER RELATIONS ----

// Used in: UserRelationsContext (for logged-in user only)
/**
 * Fetch all user relations (favorites, following, followers) for the logged-in user.
 */
export async function fetchUserRelations(idToken: string): Promise<GetUserRelationsResponse> {
  return secureFetch(`${SECURE_API_BASE}/user-relations`, idToken, { method: 'GET' }) as Promise<GetUserRelationsResponse>;
}

/**
 * Fetch paginated notifications (mixed read/unread, newest first)
 */
export async function fetchNotificationsPaginated(lastKey: string | null, idToken: string): Promise<{
  notifications: Array<{
    id: string;
    type: string;
    title: string;
    preview: string;
    full_message?: string;
    created_at: string;
    read: boolean;
    priority: string;
    actions?: Array<{ label: string; url: string; style: string }>;
    metadata?: Record<string, any>;
    // Legacy support
    alert_type?: string;
    message?: string;
    pattern_id?: string;
    snapshot_id?: string;
  }>;
  lastKey: string | null;
}> {
  const url = lastKey 
    ? `${SECURE_API_BASE}/notifications?lastKey=${encodeURIComponent(lastKey)}`
    : `${SECURE_API_BASE}/notifications`;
  return secureFetch(url, idToken, { method: 'GET' });
}

// Used in: CreatePattern page
/**
 * Migrate temp pattern images to permanent draft location
 */
export async function migrateTempImages(draftPatternId: string, tempPatternId: string, idToken: string): Promise<MigrateTempImagesResponse> {
  return secureFetch(`${SECURE_API_BASE}/pattern/${encodeURIComponent(draftPatternId)}/migrate-images`, idToken, {
    method: 'POST',
    body: JSON.stringify({ tempPatternId }),
  }) as Promise<MigrateTempImagesResponse>;
}

// Used in: CreatePattern page
/**
 * Atomically migrate temp pattern to draft with image migration
 */
export async function migrateTempToDraft(tempPatternId: string, idToken: string): Promise<TempToDraftMigrationResponse> {
  try {
    const requestData: TempToDraftMigrationRequest = { tempPatternId };
    const result = await secureFetch(`${SECURE_API_BASE}/pattern/temp-to-draft`, idToken, {
      method: 'POST',
      body: JSON.stringify(requestData),
    }) as TempToDraftMigrationResponse;
    
    // Clear localStorage temp pattern key on success
    localStorage.removeItem('tempPatternId');
    
    return result;
  } catch (error) {
    console.error('Failed to migrate temp to draft:', error);
    throw new Error('Failed to save pattern as draft');
  }
}

// ---- MODERATION ----

// Used in: ModerationDashboard
/**
 * Get all unresolved moderation items for moderators (GSI3 direct query)
 */
export async function fetchModerationQueue(idToken: string): Promise<ModerationQueueResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/unresolved/needs-review`, idToken, { method: 'GET' }) as Promise<ModerationQueueResponse>;
}

// Used in: ModerationDashboard
/**
 * Get moderation snapshot with pattern content
 */
export async function fetchModerationSnapshot(snapshotId: string, idToken: string): Promise<ModerationSnapshotResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}`, idToken, { method: 'GET' }) as Promise<ModerationSnapshotResponse>;
}

// Used in: ModerationDashboard
/**
 * Resolve a moderation item (approve/reject) - Manual review only
 */
export async function resolveModerationItem(
  snapshotId: string,
  resolution: 'approved' | 'rejected',
  notes?: string,
  userMessage?: string,
  idToken: string
): Promise<ResolveModerationResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ resolution, notes, userMessage }),
  }) as Promise<ResolveModerationResponse>;
}

// Used in: ModerationDashboard
/**
 * Acknowledge an auto-rejected item (audit-only, no pattern changes)
 */
export async function acknowledgeAutoReject(
  snapshotId: string,
  decision: 'correct' | 'incorrect',
  notes?: string,
  idToken: string
): Promise<AcknowledgeAutoRejectResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}/acknowledge`, idToken, {
    method: 'POST',
    body: JSON.stringify({ decision, notes }),
  }) as Promise<AcknowledgeAutoRejectResponse>;
}

// Used in: ModerationDashboard
/**
 * Get all snapshots for a pattern version (including auto-rejected)
 */
export async function fetchPatternModerationHistory(
  patternId: string,
  targetVersion: number,
  idToken: string
): Promise<PatternModerationHistoryResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/moderation/pattern/${encodeURIComponent(patternId)}/version/${targetVersion}/history`,
    idToken,
    { method: 'GET' }
  ) as Promise<PatternModerationHistoryResponse>;
}

// Used in: ModerationDashboard
/**
 * Get auto-rejected moderation items (GSI3 direct query for unresolved, fallback for reviewed)
 */
export async function fetchAutoRejectedQueue(idToken: string, reviewed: boolean = false): Promise<AutoRejectedQueueResponse> {
  const url = reviewed 
    ? `${SECURE_API_BASE}/moderation/auto-rejected?reviewed=true`
    : `${SECURE_API_BASE}/moderation/unresolved/auto-rejected`;
  return secureFetch(url, idToken, { method: 'GET' }) as Promise<AutoRejectedQueueResponse>;
}

// Used in: ModerationDashboard
/**
 * Get common rejection message templates
 */
export async function fetchRejectionTemplates(idToken: string): Promise<RejectionTemplatesResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/rejection-templates`, idToken, { method: 'GET' }) as Promise<RejectionTemplatesResponse>;
}

// Used in: ModerationDashboard
/**
 * Get pending flagged reviews for moderation (GSI3 direct query)
 */
export async function fetchPendingReviewFlags(idToken: string): Promise<PendingReviewFlagsResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/unresolved/flags`, idToken, { method: 'GET' }) as Promise<PendingReviewFlagsResponse>;
}

// Used in: ModerationDashboard
/**
 * Get material contributions pending moderation
 */
export async function fetchMaterialContributions(idToken: string): Promise<{ items: any[] }> {
  return secureFetch(`${SECURE_API_BASE}/moderation/material-contributions`, idToken, { method: 'GET' }) as Promise<{ items: any[] }>;
}

// Used in: ModerationDashboard
/**
 * Resolve a material contribution (approve/reject)
 */
export async function resolveMaterialContribution(
  contributionId: string,
  resolution: 'approved' | 'rejected',
  notes?: string,
  idToken: string
): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/moderation/material-contributions/${contributionId}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ resolution, notes }),
  }) as Promise<void>;
}

// Used in: ModerationDashboard
/**
 * Resolve a flagged review (dismiss flag or hide review)
 */
export async function resolveFlaggedReview(
  flagId: string,
  outcome: 'dismissed' | 'approved',
  notes?: string,
  idToken: string
): Promise<ResolveFlaggedReviewResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/flag/${encodeURIComponent(flagId)}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ outcome, notes }),
  }) as Promise<ResolveFlaggedReviewResponse>;
}

// Used in: ModerationDashboard
/**
 * Get structured moderator notes for a user
 */
export async function fetchModeratorNotes(userId: string, idToken: string): Promise<ModeratorNotesResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/notes`, idToken, { method: 'GET' }) as Promise<ModeratorNotesResponse>;
}

// Used in: ModerationDashboard
/**
 * Create a new structured note for a user
 */
export async function createUserNote(
  userId: string,
  content: string,
  patternId?: string,
  category: string = 'other',
  severity: string = 'medium',
  idToken: string
): Promise<CreateNoteResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/notes`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content, patternId, category, severity }),
  }) as Promise<CreateNoteResponse>;
}



// Used in: ModerationDashboard
/**
 * Get structured moderator notes for a pattern
 */
export async function fetchPatternNotes(patternId: string, idToken: string): Promise<PatternNotesResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/pattern/${encodeURIComponent(patternId)}/notes`, idToken, { method: 'GET' }) as Promise<PatternNotesResponse>;
}

// Used in: ModerationDashboard
/**
 * Create a new structured note for a pattern
 */
export async function createPatternNote(
  patternId: string,
  content: string,
  category: string = 'other',
  severity: string = 'medium',
  idToken: string
): Promise<CreateNoteResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/pattern/${encodeURIComponent(patternId)}/notes`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content, category, severity }),
  }) as Promise<CreateNoteResponse>;
}

// Used in: ModerationDashboard
/**
 * Get structured moderator notes for a snapshot
 */
export async function fetchSnapshotNotes(snapshotId: string, idToken: string): Promise<SnapshotNotesResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}/notes`, idToken, { method: 'GET' }) as Promise<SnapshotNotesResponse>;
}

// Used in: ModerationDashboard
/**
 * Create a new structured note for a snapshot
 */
export async function createSnapshotNote(
  snapshotId: string,
  content: string,
  category: string = 'other',
  severity: string = 'medium',
  idToken: string
): Promise<CreateNoteResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}/notes`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content, category, severity }),
  }) as Promise<CreateNoteResponse>;
}



// Used in: ModerationDashboard
/**
 * Get user behavior overview for moderation context
 */
export async function fetchUserBehaviorOverview(
  userId: string,
  idToken: string
): Promise<UserBehaviorOverviewResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/behavior-overview`, idToken, { method: 'GET' }) as Promise<UserBehaviorOverviewResponse>;
}

// Used in: ModerationDashboard
/**
 * Get pending review moderation queue (GSI3 direct query)
 */
export async function fetchReviewModerationQueue(idToken: string): Promise<ReviewModerationQueueResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/unresolved/review-moderations`, idToken, { method: 'GET' }) as Promise<ReviewModerationQueueResponse>;
}

// Used in: ModerationDashboard
/**
 * Get auto-rejected reviews
 */
export async function fetchAutoRejectedReviews(idToken: string): Promise<AutoRejectedReviewsResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/auto-rejected-reviews`, idToken, { method: 'GET' }) as Promise<AutoRejectedReviewsResponse>;
}

// Used in: ModerationDashboard
/**
 * Approve a review (content scanning passed manual review)
 */
export async function approveReview(
  reviewId: string,
  notes?: string,
  idToken: string
): Promise<ResolveReviewModerationResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/review-moderation/${encodeURIComponent(reviewId)}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ resolution: 'approved', notes }),
  }) as Promise<ResolveReviewModerationResponse>;
}

// Used in: ModerationDashboard
/**
 * Reject a review (content scanning failed manual review)
 */
export async function rejectReview(
  reviewId: string,
  notes?: string,
  userMessage?: string,
  idToken: string
): Promise<ResolveReviewModerationResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/review-moderation/${encodeURIComponent(reviewId)}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ resolution: 'rejected', notes, userMessage }),
  }) as Promise<ResolveReviewModerationResponse>;
}

// Used in: UserAnalysisPage
/**
 * Get comprehensive user analysis for moderation dashboard
 */
export async function fetchComprehensiveUserAnalysis(
  userId: string,
  filters: {
    activityTypes?: string[];
    timeRange?: string;
    riskLevels?: string[];
    search?: string;
    limit?: number;
    offset?: number;
  },
  idToken: string
): Promise<ComprehensiveUserAnalysisResponse> {
  const params = new URLSearchParams({
    limit: (filters.limit || 20).toString(),
    offset: (filters.offset || 0).toString(),
    time_range: filters.timeRange || 'all'
  })

  if (filters.activityTypes && filters.activityTypes.length > 0) {
    params.set('activity_types', filters.activityTypes.join(','))
  }
  if (filters.riskLevels && filters.riskLevels.length > 0) {
    params.set('risk_levels', filters.riskLevels.join(','))
  }
  if (filters.search && filters.search.trim()) {
    params.set('search', filters.search.trim())
  }

  return secureFetch(`${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/comprehensive-analysis?${params}`, idToken, { method: 'GET' }) as Promise<ComprehensiveUserAnalysisResponse>
}

// ---- CRAFT ROOM ----



/**
 * Update craft room details
 */
export async function updateCraftRoom(roomData: { 
  title?: string; 
  description?: string; 
  custom_tabs?: string[];
  material_tab_mapping?: Record<string, string>;
}, idToken: string): Promise<CraftRoomResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room`, idToken, {
    method: 'PUT',
    body: JSON.stringify(roomData),
  }) as Promise<CraftRoomResponse>;
}

/**
 * Get craft room materials (paginated)
 */
export async function fetchMaterials(page: number = 1, category?: string, idToken: string): Promise<{ materials: MaterialResponse[]; hasMore: boolean }> {
  let url = `${SECURE_API_BASE}/materials?page=${page}`;
  if (category && category !== 'all') {
    url += `&category=${encodeURIComponent(category)}`;
  }
  return secureFetch(url, idToken, { method: 'GET' }) as Promise<{ materials: MaterialResponse[]; hasMore: boolean }>;
}

/**
 * Get user materials (all materials for inventory checking)
 */
export async function getUserMaterials(idToken: string): Promise<MaterialResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/materials/all`, idToken, { method: 'GET' }) as Promise<MaterialResponse[]>;
}

/**
 * Get user settings including craft room preferences
 */
export async function getUserSettings(idToken: string): Promise<{ craft_room_settings?: { show_material_checker?: boolean } }> {
  return secureFetch(`${SECURE_API_BASE}/user/settings`, idToken, { method: 'GET' }) as Promise<{ craft_room_settings?: { show_material_checker?: boolean } }>;
}

/**
 * Add material to inventory
 */
export async function addMaterial(materialData: CreateMaterialRequest, idToken: string): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials`, idToken, {
    method: 'POST',
    body: JSON.stringify(materialData),
  }) as Promise<MaterialResponse>;
}

/**
 * Update material
 */
export async function updateMaterial(materialId: string, materialData: Partial<CreateMaterialRequest>, idToken: string): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(materialData),
  }) as Promise<MaterialResponse>;
}

/**
 * Delete material
 */
export async function deleteMaterial(materialId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'DELETE',
  }) as Promise<void>;
}

/**
 * Create craft project
 */
export async function createCraftProject(projectData: CreateCraftProjectRequest, idToken: string): Promise<{ projectId: string }> {
  return secureFetch(`${SECURE_API_BASE}/projects`, idToken, {
    method: 'POST',
    body: JSON.stringify(projectData),
  }) as Promise<{ projectId: string }>;
}

/**
 * Update craft project
 */
export async function updateCraftProject(projectId: string, projectData: UpdateCraftProjectRequest, idToken: string): Promise<CraftProjectResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(projectData),
  }) as Promise<CraftProjectResponse>;
}

/**
 * Get craft project
 */
export async function fetchCraftProject(projectId: string, idToken?: string): Promise<CraftProjectResponse> {
  if (idToken) {
    return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`, idToken, {
      method: 'GET',
    }) as Promise<CraftProjectResponse>;
  } else {
    // Public access for non-authenticated users
    const response = await fetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`);
    if (!response.ok) {
      throw new Error('Project not found');
    }
    return response.json();
  }
}

/**
 * Archive craft project (removes from GSI2 active projects)
 */
export async function archiveCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/archive`, idToken, {
    method: 'POST',
  }) as Promise<void>;
}

/**
 * Unarchive craft project (adds back to GSI2 active projects)
 */
export async function unarchiveCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/unarchive`, idToken, {
    method: 'POST',
  }) as Promise<void>;
}

/**
 * Get user's archived projects
 */
export async function fetchArchivedProjects(idToken: string): Promise<CraftProjectResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/projects/archived`, idToken, {
    method: 'GET',
  }) as Promise<CraftProjectResponse[]>;
}

/**
 * Get craft room privacy settings
 */
export async function fetchCraftRoomSettings(idToken: string): Promise<{ is_public: boolean; allow_material_discovery: boolean; show_purchase_info: boolean }> {
  return secureFetch(`${SECURE_API_BASE}/craft-room/settings`, idToken, { method: 'GET' }) as Promise<{ is_public: boolean; allow_material_discovery: boolean; show_purchase_info: boolean }>;
}

/**
 * Update craft room privacy settings
 */
export async function updateCraftRoomSettings(settings: { is_public?: boolean; allow_material_discovery?: boolean; show_purchase_info?: boolean; show_material_checker?: boolean }, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/craft-room/settings`, idToken, {
    method: 'PUT',
    body: JSON.stringify(settings),
  }) as Promise<void>;
}

/**
 * Upload craft room image (5MB limit, no optimization)
 */
export async function uploadCraftRoomImage(
  file: File | Blob,
  idToken: string
): Promise<{ url: string }> {
  const filename = (file instanceof File && file.name) ? file.name : 'room.jpg';
  const url = `${SECURE_API_BASE}/craft-room/image-upload?filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    credentials: 'include',
    body: file,
  });
  if (!resp.ok) {
    let errMsg = 'API error';
    try { errMsg = await resp.text(); } catch { /* empty */ }
    throw new Error(errMsg);
  }
  return resp.json();
}

/**
 * Upload material image (5MB limit, no optimization)
 */
export async function uploadMaterialImage(
  materialId: string,
  file: File | Blob,
  idToken: string
): Promise<{ url: string }> {
  const filename = (file instanceof File && file.name) ? file.name : 'material.jpg';
  const url = `${SECURE_API_BASE}/materials/image-upload?materialId=${encodeURIComponent(materialId)}&filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    credentials: 'include',
    body: file,
  });
  if (!resp.ok) {
    let errMsg = 'API error';
    try { errMsg = await resp.text(); } catch { /* empty */ }
    throw new Error(errMsg);
  }
  return resp.json();
}

/**
 * Upload temp craft project image (30-day TTL)
 */
export async function uploadTempCraftProjectImage(
  file: File | Blob,
  idToken: string
): Promise<{ url: string }> {
  const filename = (file instanceof File && file.name) ? file.name : 'project.jpg';
  const url = `${SECURE_API_BASE}/craft-projects/temp-image-upload?filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    credentials: 'include',
    body: file,
  });
  if (!resp.ok) {
    let errMsg = 'API error';
    try { errMsg = await resp.text(); } catch { /* empty */ }
    throw new Error(errMsg);
  }
  return resp.json();
}

/**
 * Upload temp material image (30-day TTL)
 */
export async function uploadTempMaterialImage(
  file: File | Blob,
  idToken: string
): Promise<{ url: string }> {
  const filename = (file instanceof File && file.name) ? file.name : 'material.jpg';
  const url = `${SECURE_API_BASE}/materials/temp-image-upload?filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    credentials: 'include',
    body: file,
  });
  if (!resp.ok) {
    let errMsg = 'API error';
    try { errMsg = await resp.text(); } catch { /* empty */ }
    throw new Error(errMsg);
  }
  return resp.json();
}

/**
 * Get craft project materials
 */
export async function fetchProjectMaterials(projectId: string, idToken: string): Promise<ProjectMaterialResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials`, idToken, {
    method: 'GET',
  }) as Promise<ProjectMaterialResponse[]>;
}

/**
 * Add material to craft project
 */
export async function addProjectMaterial(projectId: string, materialData: CreateProjectMaterialRequest, idToken: string): Promise<ProjectMaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials`, idToken, {
    method: 'POST',
    body: JSON.stringify(materialData),
  }) as Promise<ProjectMaterialResponse>;
}

/**
 * Update craft project material
 */
export async function updateProjectMaterial(projectId: string, materialId: string, materialData: UpdateProjectMaterialRequest, idToken: string): Promise<ProjectMaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(materialData),
  }) as Promise<ProjectMaterialResponse>;
}

/**
 * Delete craft project material
 */
export async function deleteProjectMaterial(projectId: string, materialId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'DELETE',
  }) as Promise<void>;
}

/**
 * Get craft project comments
 */
export async function fetchProjectComments(projectId: string): Promise<ProjectCommentResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/comments`, '', {
    method: 'GET',
  }) as Promise<ProjectCommentResponse[]>;
}

/**
 * Add comment to craft project (requires authentication)
 */
export async function addProjectComment(projectId: string, content: string, idToken: string): Promise<ProjectCommentResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-projects/${encodeURIComponent(projectId)}/comments`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content }),
  }) as Promise<ProjectCommentResponse>;
}

/**
 * Delete project comment (owner only)
 */
export async function deleteProjectComment(projectId: string, commentId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(commentId)}`, idToken, {
    method: 'DELETE',
  }) as Promise<void>;
}

/**
 * Get single material details
 */
export async function fetchMaterial(materialId: string, idToken: string): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'GET',
  }) as Promise<MaterialResponse>;
}

/**
 * Get material usage history in projects
 */
export async function fetchMaterialUsageHistory(materialId: string, idToken: string): Promise<Array<{
  project_id: string;
  project_title: string;
  pattern_id?: string;
  pattern_title?: string;
  quantity_used: number;
  unit: string;
  notes?: string;
  would_recommend?: boolean;
  used_at: string;
}>> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}/usage-history`, idToken, {
    method: 'GET',
  }) as Promise<Array<{
    project_id: string;
    project_title: string;
    pattern_id?: string;
    pattern_title?: string;
    quantity_used: number;
    unit: string;
    notes?: string;
    would_recommend?: boolean;
    used_at: string;
  }>>;
}
// Craft Room Moderation API functions (minimal additions)

export async function fetchCraftRoomModerationQueue(
  queueType: 'needs_review' | 'auto_rejected',
  idToken: string
): Promise<any[]> {
  return secureFetch(`${SECURE_API_BASE}/moderation/craft-room/unresolved/${queueType}`, idToken, { method: 'GET' }) as Promise<any[]>;
}

export async function resolveCraftRoomModeration(
  moderationId: string,
  resolution: 'approved' | 'rejected' | 'acknowledged',
  notes: string | undefined,
  idToken: string
): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/moderation/craft-room/${moderationId}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ resolution, notes }),
  }) as Promise<void>;
}


// ---- USER SETTINGS ----

export async function fetchUserSettingsDetailed(idToken: string): Promise<{
  inventory_settings?: any;
  profile_privacy?: any;
  project_privacy?: any;
}> {
  return secureFetch(`${SECURE_API_BASE}/user/settings`, idToken, { method: 'GET' });
}

export async function updateUserSettingsDetailed(settings: {
  inventory_settings?: any;
  profile_privacy?: any;
  project_privacy?: any;
}, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/user/settings`, idToken, {
    method: 'PUT',
    body: JSON.stringify(settings),
  }) as Promise<void>;
}

// ---- MATERIAL BATCH OPERATIONS ----

/**
 * Batch update multiple materials
 */
export async function batchUpdateMaterials(
  changes: Record<string, any>,
  idToken: string
): Promise<{ message: string; updated: number }> {
  return secureFetch(`${SECURE_API_BASE}/materials/batch-update`, idToken, {
    method: 'POST',
    body: JSON.stringify({ changes }),
  }) as Promise<{ message: string; updated: number }>;
}

/**
 * Bulk delete multiple materials
 */
export async function bulkDeleteMaterials(
  materialIds: string[],
  idToken: string
): Promise<{ message: string; deleted: number }> {
  return secureFetch(`${SECURE_API_BASE}/materials/bulk-delete`, idToken, {
    method: 'POST',
    body: JSON.stringify({ materialIds }),
  }) as Promise<{ message: string; deleted: number }>;
}

/**
 * Delete craft project
 */
export async function deleteCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`, idToken, {
    method: 'DELETE',
  }) as Promise<void>;
}

// ---- FOLDER MANAGEMENT ----

/**
 * Get user's folder configuration
 */
export async function getFolderConfig(idToken: string): Promise<Array<{ name: string; icon?: string; color?: string }>> {
  return secureFetch(`${SECURE_API_BASE}/folders/config`, idToken, {
    method: 'GET',
  }) as Promise<Array<{ name: string; icon?: string; color?: string }>>;
}

/**
 * Update user's folder configuration
 */
export async function updateFolderConfig(
  folders: Array<{ name: string; icon?: string; color?: string }>,
  idToken: string
): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/folders/config`, idToken, {
    method: 'PUT',
    body: JSON.stringify({ folders }),
  }) as Promise<void>;
}

/**
 * Assign materials to folders
 */
export async function assignMaterialsToFolders(
  materialIds: string[],
  folderNames: string[],
  idToken: string
): Promise<{ message: string; updated: number }> {
  return secureFetch(`${SECURE_API_BASE}/folders/assign`, idToken, {
    method: 'POST',
    body: JSON.stringify({ materialIds, folderNames }),
  }) as Promise<{ message: string; updated: number }>;
}

// ---- INVITE REQUESTS ----

/**
 * Get access requests (moderators only)
 */
export async function getAccessRequests(idToken: string): Promise<Array<{
  feedback_id: string;
  email: string;
  message_type: 'request_access' | 'feedback';
  moderation: 'access_requests' | 'general_feedback';
  feedback_type: string;
  status: string;
  created_at: string;
  message?: string;
}>> {
  return secureFetch(`${SECURE_API_BASE}/access-requests`, idToken, {
    method: 'GET',
  }) as Promise<Array<{
    feedback_id: string;
    email: string;
    message_type: 'request_access' | 'feedback';
    moderation: 'access_requests' | 'general_feedback';
    feedback_type: string;
    status: string;
    created_at: string;
    message?: string;
  }>>;
}

/**
 * Get feedback (moderators only)
 */
export async function getFeedback(idToken: string): Promise<Array<{
  feedback_id: string;
  email: string;
  message_type: 'request_access' | 'feedback';
  moderation: 'access_requests' | 'general_feedback';
  feedback_type: string;
  status: string;
  created_at: string;
  message?: string;
}>> {
  return secureFetch(`${SECURE_API_BASE}/feedback`, idToken, {
    method: 'GET',
  }) as Promise<Array<{
    feedback_id: string;
    email: string;
    message_type: 'request_access' | 'feedback';
    moderation: 'access_requests' | 'general_feedback';
    feedback_type: string;
    status: string;
    created_at: string;
    message?: string;
  }>>;
}

/**
 * Send invite email (moderators only)
 */
export async function sendInviteEmail(
  feedbackId: string,
  idToken: string
): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/access-requests/${feedbackId}/send-invite`, idToken, {
    method: 'POST',
  }) as Promise<void>;
}

/**
 * Resolve feedback (moderators only)
 */
export async function resolveFeedback(
  feedbackId: string,
  resolution: 'resolved' | 'dismissed',
  notes?: string,
  idToken: string
): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/feedback/${feedbackId}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ resolution, notes }),
  }) as Promise<void>;
}